import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import MultiComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/multi-combo-box';
import Label from 'atom-components/label';
import { organisationUsersWithMarkReviewerRoleParams } from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';

const fieldLabel = 'Reviewer(s)';
const fieldName = 'reviewerOrganisationUserIds';

function useSelectableReviewerOrganisationUsers(): {
  selectableOrganisationUsers: ComboBoxItem[];
  isLoading: boolean;
} {
  // Get the current user's organisation:
  const currentOrganisation = useAuthCustomer();

  // Get organisation users who are reviewers.
  const organisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkReviewerRoleParams(currentOrganisation?.id)
  );

  const selectableOrganisationUsers: ComboBoxItem[] =
    organisationUsersQuery.data?.json.map((organisationUser) => ({
      id: organisationUser.id,
      name: `${organisationUser.user.firstname} ${organisationUser.user.lastname}`
    })) || [];

  const isLoading = isInitialLoad(organisationUsersQuery);

  return { selectableOrganisationUsers, isLoading };
}

type Props = {
  triggerFormSubmission: () => void;
};

export default function ReviewerFilter({ triggerFormSubmission }: Props): ReactElement {
  const { control, setValue } = useFormContext();
  const { selectableOrganisationUsers, isLoading } = useSelectableReviewerOrganisationUsers();

  return (
    <div>
      <Label htmlFor={fieldName}>{fieldLabel}</Label>
      <MultiComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableOrganisationUsers}
        clearable
        loading={isLoading}
        onChange={(items): void => {
          const reviewerOrganisationUserIds = items.map((item) => item.id);
          setValue(fieldName, reviewerOrganisationUserIds, {
            shouldValidate: true,
            shouldTouch: true
          });
          triggerFormSubmission();
        }}
      />
    </div>
  );
}

export function ReviewerAppliedFilter({ triggerFormSubmission }): ReactElement | null {
  const { watch, setValue } = useFormContext();
  const reviewerOrganisationUserIds = watch(fieldName);
  const { selectableOrganisationUsers } = useSelectableReviewerOrganisationUsers();

  if (reviewerOrganisationUserIds.length === 0) return null;

  return (
    <AppliedFilters.Filter name={fieldLabel}>
      {reviewerOrganisationUserIds.map(
        (organisationUserId: number, index: number): ReactElement => (
          <AppliedFilters.FilterItem
            key={organisationUserId}
            onClick={(): void => {
              const reviewerOrganisationUserIdsCopy = [...reviewerOrganisationUserIds]; // Todo: Not needed once we change to toSpliced
              reviewerOrganisationUserIdsCopy.splice(index, 1);
              setValue(fieldName, reviewerOrganisationUserIdsCopy);
              triggerFormSubmission();
            }}
          >
            {selectableOrganisationUsers.find((ou) => ou.id === organisationUserId)?.name}
          </AppliedFilters.FilterItem>
        )
      )}
    </AppliedFilters.Filter>
  );
}
